// Thin fetch wrapper around the Pages Functions API. Cookies are same-origin.

export type Role = "viewer" | "contributor" | "editor" | "admin";
export const ROLE_RANK: Record<Role, number> = { viewer: 0, contributor: 1, editor: 2, admin: 3 };
export const roleAtLeast = (role: Role, min: Role) => ROLE_RANK[role] >= ROLE_RANK[min];

export interface Me {
  sub: string;
  name: string;
  role: Role;
}

export interface ArticleDTO {
  id: string;
  authorSub: string;
  authorName?: string;
  title: string;
  blocks: any[];
  status: "draft" | "submitted";
  createdAt: number;
  updatedAt: number;
  submittedAt: number | null;
}

export interface NewsletterMeta {
  id: string;
  slug: string;
  title: string;
  status: "draft" | "published";
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  publishedAt: number | null;
}

export interface NewsletterDTO extends NewsletterMeta {
  head: any;
  blocks: any[];
}

export interface UserDTO {
  sub: string;
  name: string;
  role: Role;
  createdAt: number;
  lastLoginAt: number | null;
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { "content-type": "application/json", ...(init?.headers || {}) },
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiError(res.status, data?.error || res.statusText);
  return data as T;
}

export const api = {
  me: () => req<Me>("/api/me"),
  logout: () => req<void>("/api/auth/logout", { method: "POST" }),

  listArticles: () => req<{ articles: ArticleDTO[] }>("/api/articles").then((r) => r.articles),
  createArticle: (title: string) =>
    req<{ article: ArticleDTO }>("/api/articles", { method: "POST", body: JSON.stringify({ title }) }).then((r) => r.article),
  getArticle: (id: string) => req<{ article: ArticleDTO }>(`/api/articles/${id}`).then((r) => r.article),
  saveArticle: (id: string, patch: { title?: string; blocks?: any[] }) =>
    req<{ article: ArticleDTO }>(`/api/articles/${id}`, { method: "PUT", body: JSON.stringify(patch) }).then((r) => r.article),
  deleteArticle: (id: string) => req<void>(`/api/articles/${id}`, { method: "DELETE" }),
  submitArticle: (id: string, withdraw = false) =>
    req<{ article: ArticleDTO }>(`/api/articles/${id}/submit`, { method: "POST", body: JSON.stringify({ withdraw }) }).then((r) => r.article),

  listNewsletters: () => req<{ newsletters: NewsletterMeta[] }>("/api/newsletters").then((r) => r.newsletters),
  createNewsletter: (title: string) =>
    req<{ newsletter: NewsletterDTO }>("/api/newsletters", { method: "POST", body: JSON.stringify({ title }) }).then((r) => r.newsletter),
  getNewsletter: (id: string) => req<{ newsletter: NewsletterDTO }>(`/api/newsletters/${id}`).then((r) => r.newsletter),
  getNewsletterBySlug: (slug: string) =>
    req<{ newsletter: NewsletterDTO }>(`/api/newsletters/by-slug/${slug}`).then((r) => r.newsletter),
  saveNewsletter: (id: string, patch: { title?: string; head?: any; blocks?: any[] }) =>
    req<{ newsletter: NewsletterDTO }>(`/api/newsletters/${id}`, { method: "PUT", body: JSON.stringify(patch) }).then((r) => r.newsletter),
  deleteNewsletter: (id: string) => req<void>(`/api/newsletters/${id}`, { method: "DELETE" }),
  addArticleToNewsletter: (id: string, articleId: string) =>
    req<{ newsletter: NewsletterDTO; added: number }>(`/api/newsletters/${id}/add-article`, { method: "POST", body: JSON.stringify({ articleId }) }),
  publishNewsletter: (id: string, published: boolean) =>
    req<{ newsletter: NewsletterDTO }>(`/api/newsletters/${id}/publish`, { method: "POST", body: JSON.stringify({ published }) }).then((r) => r.newsletter),

  listUsers: () => req<{ users: UserDTO[] }>("/api/users").then((r) => r.users),
  setUserRole: (sub: string, role: Role) =>
    req<{ user: UserDTO }>(`/api/users/${sub}`, { method: "PATCH", body: JSON.stringify({ role }) }).then((r) => r.user),

  uploadImage: async (file: File): Promise<string> => {
    const form = new FormData();
    form.append("file", file);
    const res = await fetch("/api/uploads", { method: "POST", body: form });
    const data = await res.json();
    if (!res.ok) throw new ApiError(res.status, data?.error || "Upload failed");
    return data.url as string;
  },
};
